import { ActionContext } from 'vuex';
import { Geolocation } from '@capacitor/geolocation';
import { Address, Coordinate, CustomerAddress } from '@/models/addressModels';
import {
    addAccountAddressPost,
    editAccountAddressPost,
    addCreditCard,
    deleteAddress,
    deleteStoredCard,
    fetchAccountInfo,
    fetchStoredCards,
    updateStoredCard,
    addRewardToBasket,
    getRewardBasketItems,
    removeItemsFromBasket,
    getRewardHistory,
} from '@/services/accountService';
import { deleteLocalStorage, getLocalStorage, localStorageEnum, replaceLocalStorage } from '@/services/localStorageService';
import { DEFAULT_COORDINATES, rewardTypes } from '@/constants';
import useAuth from '@/services/authService';
import useVault from '@/services/vaultService';
import router from '@/router';
import { CheckoutRequest } from '@/models/checkoutModels';
import { validateDeliveryAddress } from '@/services/locationService';
import { Account } from '@/models/account/authModels';
import { alertController } from '@ionic/vue';
import { CreditCard } from '@/models/PaymentModels';
import { errorToast } from '@/services/pageService';
import { HistoryOrder, OrderLogistics } from '@/models/cartModels';
import { orderHistory } from '@/services/orderService';
import { syncUserToBraze } from '@/services/braze/brazeSync';
import { fetchRewardsInfo } from '@/services/accountService';
import { AddRewardPayload, BasketItem, Reward, RewardsInfo, RewardHistory, RemoveRewardAction, AddRewardAction, RewardsBasketInfo, RewardErrors } from '@/models/account/rewardModels';
import { RewardsInfoDTO, DiscountBasketItemDTO, RewardDTO, RewardHistoryDTO } from '@/models/account/rewardDTOModels';
import { OrderState } from './orderModule';
import { formatRelativeDate } from '@/services/timeService';

const { isAuthenticated, getCognitoUserInfo, logout, isAccessTokenExpired, isRefreshTokenAvailable, refreshSession, clearStorage } = useAuth();
const { initializeUnlockMode } = useVault();
let authReady = false;

export interface AccountState {
    loginProcessing: boolean;
    loyaltyProcessing: boolean;
    loyaltyInfoProcessing: boolean;
    createAccountProcessing: boolean;
    isOrderHistoryLoading: boolean;
    account?: Account;
    orderHistory?: HistoryOrder[];
    guest: Account;
    userLocation?: Coordinate;
    isAuthenticated: boolean;
    isGeoLocationOn: boolean;
    appInfo?: any;
    rewardsInfo?: RewardsInfo;
    rewardErrors: RewardErrors;
    rewardsBasketInfo: RewardsBasketInfo | null;
    isRewardsLocked: boolean;
    updatingRewardBasket: boolean;
    isRewardsHistoryLoading: boolean;
    rewardHistory: ReadonlyArray<RewardHistory> | null;
}

const AccountState: AccountState = {
    loginProcessing: false,
    loyaltyProcessing: false,
    loyaltyInfoProcessing: true,
    createAccountProcessing: false,
    isOrderHistoryLoading: false,
    isAuthenticated: false,
    isGeoLocationOn: false,
    rewardErrors: {
        hasInfoError: false,
        hasBasketError: false,
        hasHistoryError: false,
    },
    rewardsBasketInfo: null,
    guest: new Account(),
    isRewardsLocked: false,
    updatingRewardBasket: false,
    isRewardsHistoryLoading: true,
    rewardHistory: null,
};

const initUserLogin = async (context) => {
    context.commit('SET_LOGIN_PROCESSING', true);
    try {
        const user = await getCognitoUserInfo();
        if (user) {
            const accountInfoResponse = await fetchAccountInfo(1);
            if (accountInfoResponse) {
                context.commit('SET_ACCOUNT_INFO', accountInfoResponse);
                const cart = getLocalStorage(localStorageEnum.cart);
                const localCartIsEmpty = !cart?.lineItems || cart.lineItems.length === 0;

                const serverCartLineItems = accountInfoResponse.cart?.orderDetails?.lineItems ?? [];
                const serverCartIsEmpty = Array.isArray(serverCartLineItems) ? serverCartLineItems.length === 0 : true;

                if (localCartIsEmpty && serverCartIsEmpty) {
                    context.dispatch('clearAllRewardBasketItems');
                }

                if (cart && cart.lineItems) {
                    context.dispatch('initCart', cart.lineItems);
                }
                context.dispatch('fetchOrderHistory');

                // @ts-ignore
                const orderLogistics = new OrderLogistics(accountInfoResponse.cart.orderDetails?.orderLogistics);

                const previousSelectedLocation = context.getters.getSelectedLocation;
                if (previousSelectedLocation) {
                    orderLogistics.storeNumber = previousSelectedLocation.id;
                }
                context.dispatch('updateEstimatedTime', accountInfoResponse.cart.orderHeader?.submitResult.promiseTime.date);
                context.dispatch('initStoreLocation', orderLogistics);
                context.dispatch('setSavedCards').catch(() => false);

                // Sync user to Braze after successful login
                try {
                    await syncUserToBraze(accountInfoResponse);
                } catch (brazeErr) {
                    // Silently fail - Braze sync is non-critical
                }
            }
        }
    } catch (err: any) {
        errorToast(err);
    } finally {
        context.commit('SET_LOGIN_PROCESSING', false);
    }
    return;
};

const initGuestUser = (context) => {
    const cart = getLocalStorage(localStorageEnum.cart);
    if (cart && cart?.lineItems) {
        context.dispatch('initCart', cart?.lineItems);
    }
    let orderDetail: OrderLogistics = getLocalStorage(localStorageEnum.orderLogistics);
    if (!orderDetail) {
        // If no orderDetail exists in localStorage, add it.
        replaceLocalStorage(localStorageEnum.orderLogistics, context.getters.getOrderLogistics, 0.1);
        orderDetail = getLocalStorage(localStorageEnum.orderLogistics);
    }
    context.dispatch('initStoreLocation', orderDetail ? new OrderLogistics(orderDetail) : undefined);
};

export default {
    state: AccountState,
    mutations: {
        SET_LOGIN_PROCESSING: (state: AccountState, isProcessing: boolean): void => {
            state.loginProcessing = isProcessing;
        },
        SET_LOGIN_REDIRECT_URL: (state: AccountState, url: string): void => {
            replaceLocalStorage(localStorageEnum.loginRedirectUrl, url, 1);
        },
        RESET_LOGIN_REDIRECT_URL: (): void => {
            replaceLocalStorage(localStorageEnum.loginRedirectUrl, '/', 1);
        },
        SET_LOYALTY_PROCESSING: (state: AccountState, isProcessing: boolean): void => {
            state.loyaltyProcessing = isProcessing;
        },
        SET_LOYALTY_INFO_PROCESSING: (state: AccountState, isProcessing: boolean): void => {
            state.loyaltyInfoProcessing = isProcessing;
        },
        SET_INFO_REWARD_ERROR: (state: AccountState, hasError: boolean): void => {
            state.rewardErrors.hasInfoError = hasError;
        },
        SET_BASKET_REWARD_ERROR: (state: AccountState, hasError: boolean): void => {
            state.rewardErrors.hasBasketError = hasError;
        },
        SET_HISTORY_REWARD_ERROR: (state: AccountState, hasError: boolean): void => {
            state.rewardErrors.hasHistoryError = hasError;
        },
        CLEAR_ACCOUNT_INFO: (state: AccountState): void => {
            state.account = undefined;
            state.orderHistory = undefined;
            state.isAuthenticated = false;
            deleteLocalStorage(localStorageEnum.customerName); // Dynatrace Data
            deleteLocalStorage(localStorageEnum.customerPhone); // Dynatrace Data
        },
        SET_ACCOUNT_INFO: (state: AccountState, info: Account): void => {
            state.account = info;
            state.isAuthenticated = !!info;
            replaceLocalStorage(localStorageEnum.customerName, info?.name, 0.04); // Dynatrace Data
            replaceLocalStorage(localStorageEnum.customerPhone, info?.phoneNumber, 0.04); // Dynatrace Data
        },
        SET_ORDER_HISTORY_LOADING: (state: AccountState, isLoading: boolean): void => {
            state.isOrderHistoryLoading = isLoading;
        },
        SET_ORDER_HISTORY: (state: AccountState, history: any): void => {
            const orderHistory: Array<HistoryOrder> = history.map((o) => new HistoryOrder(o));
            state.orderHistory = orderHistory;
        },
        SET_REWARDS_ACCOUNT_INFO: (state: AccountState, info: RewardsInfoDTO): void => {
            const rewards: ReadonlyArray<Reward> = new Array<RewardDTO>(...info.rewards)
                .sort((a, b) => new Date(a.expiringAt).getTime() - new Date(b.expiringAt).getTime())
                .map((r) => {
                    return {
                        id: r.rewardId,
                        name: r.name,
                        description: r.description,
                        imageUrl: r.rewardImageUrl,
                        discountAmount: r.discountAmount,
                        expirationDate: r.expiringAt,
                        type: rewardTypes.REWARD,
                        points: 0,
                    };
                });
            const redeemables: ReadonlyArray<Reward> = info.redeemables
                ?.filter((r) => r.applicableAsLoyaltyRedemption)
                .sort((a, b) => a.points - b.points)
                .map((r) => {
                    return {
                        id: r.redeemableId,
                        name: r.name,
                        description: r.description,
                        imageUrl: r.image,
                        discountAmount: r.discountAmount,
                        expirationDate: r.expiringAt,
                        type: rewardTypes.REDEEMABLE,
                        points: r.points,
                    };
                });
            let nextPoints = 0;
            if (redeemables.length > 0) {
                for (let i = 0; i < redeemables.length; i++) {
                    const redeemable = redeemables[i];
                    if (redeemable.points > info.accountBalance.redeemablePoints) {
                        nextPoints = redeemable.points;
                        break;
                    }
                }
            }
            const rewardsInfo: RewardsInfo = {
                rewards: rewards,
                redeemables: redeemables,
                badges: info.challengeBadges.map((b) => {
                    return {
                        id: b.badgeId,
                        name: b.name?.split('\u2013')[0].trim(),
                        imageUrl: b.image_url,
                        iconUrl: b.icon_url,
                    };
                }),
                accountBalance: {
                    totalPoints: info.accountBalance.redeemablePoints,
                },
                nextRewardThresholdPoints: nextPoints,
            };
            state.rewardsInfo = rewardsInfo;
        },
        SET_BASKET_REWARD_INFO: (state: AccountState, items: ReadonlyArray<DiscountBasketItemDTO>): void => {
            const basketItems = items.map((item) => {
                return {
                    basketItemId: item.discountBasketItemId,
                    discountType: item.discountType,
                    discountId: item.discountId,
                    name: item.discountDetails.name,
                    description: item.discountDetails.description,
                    points: item.discountDetails.points,
                    image: item.discountDetails.image,
                };
            });
            const redeemableItems = basketItems.filter((items) => items.discountType === rewardTypes.REDEEMABLE);
            state.rewardsBasketInfo = {
                basketItems: basketItems,
                pointsRedeemed: redeemableItems.reduce((acc: number, item: BasketItem) => acc + (item.points ?? 0), 0),
            };
        },
        CLEAR_REWARDS_BASKET_INFO: (state: AccountState): void => {
            state.rewardsBasketInfo = null;
            state.isRewardsLocked = false;
        },
        SET_REWARDS_LOCKED: (state: AccountState, isLocked: boolean): void => {
            state.isRewardsLocked = isLocked;
        },
        SET_UPDATING_REWARD_BASKET: (state: AccountState, val: boolean): void => {
            state.updatingRewardBasket = val;
        },
        SET_REWARDS_HISTORY_LOADING: (state: AccountState, isLoading: boolean): void => {
            state.isRewardsHistoryLoading = isLoading;
        },
        SET_REWARD_HISTORY: (state: AccountState, history: ReadonlyArray<RewardHistoryDTO>): void => {
            const rewardHistory = history.map((item, index) => {
                let eventValue = '';
                if (item.event_value.includes('(')) {
                    const absoluteValue = item.event_value.split('(')[1].split(')')[0];
                    eventValue = '-' + absoluteValue;
                } else {
                    const absoluteValue = item.event_value.split(' ')[0];
                    eventValue = absoluteValue;
                }
                let name = '';
                let imageName = '';
                let storeName: string | null = null;
                if (item.event_title === 'Item Gifted') {
                    name = item.description.split(':')[1].split('(')[0].trim();
                    imageName = 'reward_issued.png';
                } else if (item.event_title === 'Item Redeemed' || item.event_title === 'Rewards Redeemed') {
                    const descriptionParts = item.description.split('redeemed');
                    name = descriptionParts.length > 1 ? descriptionParts[0].trim() : item.event_title;
                    imageName = 'reward_expired.png';
                } else if (item.event_title === 'Points Earned') {
                    const descriptionParts = item.description.split('your')[1].split('at');
                    name = descriptionParts[0].trim();
                    imageName = 'points_earned.png';
                    storeName = descriptionParts[1].trim();
                } else if (item.event_title === 'Bonus points Earned') {
                    name = 'Bonus Points Earned';
                    imageName = 'reward_issued.png';
                } else {
                    name = 'Misc';
                    imageName = 'default.png';
                }

                const rewardHistoryItem: RewardHistory = {
                    id: index,
                    date: item.date,
                    displayDate: formatRelativeDate(item.date),
                    name: name,
                    expirationDate: item.event_title === 'Item Gifted' ? formatRelativeDate(item.event_expiry) : 'None',
                    expired: item.expired,
                    storeName: storeName ?? 'n/a',
                    imageName: imageName,
                    eventTitle: item.event_title,
                    description: item.description,
                    eventType: item.event_type,
                    eventValue: eventValue,
                    channel: item.channel,
                };
                return rewardHistoryItem;
            });
            state.rewardHistory = rewardHistory;
        },
        RESET_REWARD_HISTORY: (state: AccountState): void => {
            state.rewardHistory = null;
        },
        SET_USER_LOCATION: (state: AccountState, location: Coordinate) => {
            state.userLocation = new Coordinate(location);
        },
        ADD_ACCOUNT_ADDRESS: (state: AccountState, location: CustomerAddress) => state.account?.deliveryAddresses.push(location),
        EDIT_ACCOUNT_ADDRESS: (state: AccountState, location: CustomerAddress) => {
            const editedIndex = state.account?.deliveryAddresses.findIndex((address: CustomerAddress) => address.addressId == location.addressId);
            //@ts-ignore
            state.account?.deliveryAddresses.splice(editedIndex, 1, location);
        },
        DELETE_ACCOUNT_ADDRESS: (state: AccountState, location: CustomerAddress) => {
            const removeIndex = state.account?.deliveryAddresses?.findIndex((address) => address == location);
            if (removeIndex != undefined) state.account?.deliveryAddresses.splice(removeIndex, 1);
        },
        ADD_TEMP_ACCOUNT_DETAIL: (state: AccountState, details: CheckoutRequest): void => {
            if (state.account) {
                state.account.tempFirstName = details.firstName;
                state.account.tempLastName = details.lastName;
                state.account.tempPhone = details.phoneNumber;
                state.account.tempEmail = details.email;
                state.account.isMarketingEmail = details.isMarketingEmail;
            } else {
                state.guest.firstName = details.firstName;
                state.guest.lastName = details.lastName;
                state.guest.tempPhone = details.phoneNumber;
                state.guest.tempEmail = details.email;
                state.guest.isMarketingEmail = details.isMarketingEmail;
            }
            replaceLocalStorage(localStorageEnum.customerName, `${details?.firstName} ${details?.lastName}`, 0.4); // Dynatrace Data
            replaceLocalStorage(localStorageEnum.customerPhone, details?.phoneNumber, 0.4); // Dynatrace Data
        },
        ADD_SAVED_CARD: (state: AccountState, card: CreditCard) => state.account?.savedCards.push(card),
        SET_SAVED_CARDS: (state: AccountState, cards: CreditCard[]) => {
            if (state.account) {
                state.account.savedCards = cards;
            }
        },
        UPDATE_SAVED_CARD: (state: AccountState, card: CreditCard) => {
            if (state.account) {
                const cardIndex = state.account.savedCards.findIndex((c: CreditCard) => c.creditCardId == card.creditCardId);
                state.account.savedCards[cardIndex] = card;
            }
        },
        DELETE_SAVED_CARD: (state: AccountState, card: CreditCard) => {
            const { savedCards } = state.account ?? {};
            const creditCardId = card?.creditCardId;
            if (savedCards && creditCardId) {
                const removeIndex = savedCards.findIndex((c) => c.creditCardId === creditCardId);
                if (removeIndex !== -1) {
                    savedCards.splice(removeIndex, 1);
                }
            }
        },
        UPDATE_GEOLOCATION_STATUS: (state: AccountState, isOn: boolean) => {
            state.isGeoLocationOn = isOn;
        },
        SET_APP_INFO: (state: AccountState, info: any) => {
            state.appInfo = info;
        },
    },
    actions: {
        /**
         * Loads logged-in user account data from api or guest data if stored local storage.
         * This cascades into loading selected location, order details, cart, and menu if applicable.
         * - If auth token is expired, this is where unlock mode for native device is set to use biometric login
         */
        async initUserData(context: ActionContext<AccountState, any>): Promise<void> {
            await initializeUnlockMode(); // set the identity vaults unlock mode;
            const isAuth = await isAuthenticated(); // check if authed, will use refresh token if available
            const hasExpiredAccessToken = await isAccessTokenExpired();
            if (hasExpiredAccessToken) {
                if (await isRefreshTokenAvailable()) {
                    await refreshSession();
                } else {
                    await clearStorage();
                }
            } else if (isAuth) {
                await initUserLogin(context);
            } else {
                initGuestUser(context);
            }
            authReady = true;
            return;
        },
        async waitForAuthReady(): Promise<void> {
            return new Promise((resolve) => {
                let timeOutWait = 0;
                const checkAuthReady = () => {
                    if (authReady) {
                        //auth is ready proceed to navigation
                        resolve();
                    } else {
                        //Auth is not ready, waiting...
                        timeOutWait += 100;
                        if (timeOutWait >= 5000) {
                            authReady = true; // Set authReady to true to prevent further waiting
                        }
                        setTimeout(checkAuthReady, 100);
                    }
                };
                checkAuthReady();
            });
        },
        async setRewardsAccount(context: ActionContext<AccountState & OrderState, any>): Promise<void> {
            context.commit('SET_LOYALTY_INFO_PROCESSING', true);
            try {
                const rewardsInfo: RewardsInfo = await fetchRewardsInfo();
                context.commit('SET_INFO_REWARD_ERROR', false);
                context.commit('SET_REWARDS_ACCOUNT_INFO', rewardsInfo);
            } catch (error: any) {
                context.commit('SET_INFO_REWARD_ERROR', true);
            } finally {
                context.commit('SET_LOYALTY_INFO_PROCESSING', false);
            }
        },
        async setBasketRewardItems(context: ActionContext<AccountState, any>, validate = false): Promise<void> {
            context.commit('SET_REWARDS_VALIDATING', true);
            try {
                const basketItemsResponse = await getRewardBasketItems();
                const basketItemsDTO: ReadonlyArray<DiscountBasketItemDTO> = basketItemsResponse.discountBasketItems;

                context.commit('SET_REWARDS_LOCKED', false);
                context.commit('SET_BASKET_REWARD_ERROR', false);
                context.commit('SET_BASKET_REWARD_INFO', basketItemsDTO);
                if (validate && basketItemsDTO.length > 0) {
                    context.dispatch('validateBasketItems');
                } else {
                    context.commit('SET_REWARDS_VALIDATING', false);
                }
            } catch (error: any) {
                if (error?.includes('locked_basket')) {
                    context.commit('SET_REWARDS_LOCKED', true);
                } else {
                    context.commit('SET_REWARDS_LOCKED', false);
                    context.commit('SET_BASKET_REWARD_ERROR', true);
                }
                context.commit('SET_REWARDS_VALIDATING', false);
            }
        },
        async addRewardToBasket(context: ActionContext<AccountState, any>, payload: AddRewardAction): Promise<void> {
            const basketRewardPayload: AddRewardPayload = { discountId: payload.rewardId, discountType: payload.type };
            context.commit('SET_UPDATING_REWARD_BASKET', true);
            try {
                const basketResponse = await addRewardToBasket(basketRewardPayload);
                const basketItemsDTO: ReadonlyArray<DiscountBasketItemDTO> = basketResponse.discountBasketItems;

                context.commit('SET_BASKET_REWARD_INFO', basketItemsDTO);
                if (payload.validate) {
                    context.dispatch('validateBasketItems');
                }
            } catch (error: any) {
                if (error.includes('coupon') || error.includes('stack')) {
                    errorToast('Sorry, you cannot use a reward with a coupon. Please remove your coupon first.');
                } else if (error.includes('Points Balance is insufficient to process request.')) {
                    errorToast('Insufficient points to add this reward. Earn more points or remove another reward.');
                } else {
                    errorToast('Unable to add reward at this time. Please try again later.');
                }
            } finally {
                context.commit('SET_UPDATING_REWARD_BASKET', false);
            }
        },
        async removeRewardFromBasket(context: ActionContext<AccountState & OrderState, any>, payload: RemoveRewardAction): Promise<void> {
            const basketItems = context.state.rewardsBasketInfo?.basketItems ?? [];
            const matchingItem = basketItems.find((item) => item.discountId == payload.rewardId);
            if (matchingItem) {
                try {
                    context.commit('SET_UPDATING_REWARD_BASKET', true);
                    const removeResponse = await removeItemsFromBasket([matchingItem.basketItemId]);
                    const basketItemsDTO: ReadonlyArray<DiscountBasketItemDTO> = removeResponse.discountBasketItems;
                    context.commit('SET_BASKET_REWARD_INFO', basketItemsDTO);

                    if (basketItemsDTO.length === 0) {
                        context.commit('SET_VALIDATED_BASKET_ITEMS', []);
                        context.commit('SET_BASKET_ITEM_FAILURES', []);
                        context.commit('SET_REWARD_DISCOUNT', 0);
                    }
                    if (payload.validate) {
                        context.dispatch('validateBasketItems');
                    }
                } catch (error: any) {
                    errorToast('Unable to remove reward at this time. Please try again later.');
                } finally {
                    context.commit('SET_UPDATING_REWARD_BASKET', false);
                }
            } else {
                errorToast('Reward item not found.');
            }
        },
        async clearAllRewardBasketItems(context: ActionContext<AccountState, any>): Promise<void> {
            try {
                context.commit('SET_UPDATING_REWARD_BASKET', true);

                const basketItemsResponse = await getRewardBasketItems();
                const basketItemsDTO: ReadonlyArray<DiscountBasketItemDTO> = basketItemsResponse.discountBasketItems ?? [];

                const basketItemIds = basketItemsDTO.map((item) => item.discountBasketItemId);

                if (basketItemIds.length > 0) {
                    await removeItemsFromBasket(basketItemIds);
                }
                context.commit('CLEAR_REWARDS_BASKET_INFO');
            } catch (e) {
                context.commit('CLEAR_REWARDS_BASKET_INFO');
            } finally {
                context.commit('SET_UPDATING_REWARD_BASKET', false);
            }
        },
        async getRewardHistory(context: ActionContext<AccountState, any>): Promise<void> {
            context.commit('SET_REWARDS_HISTORY_LOADING', true);
            try {
                const rewardHistory: ReadonlyArray<RewardHistoryDTO> = await getRewardHistory();
                context.commit('SET_REWARD_HISTORY', rewardHistory);
                context.commit('SET_HISTORY_REWARD_ERROR', false);
            } catch (error: any) {
                context.commit('RESET_REWARD_HISTORY');
                context.commit('SET_HISTORY_REWARD_ERROR', true);
            } finally {
                context.commit('SET_REWARDS_HISTORY_LOADING', false);
            }
        },
        setGeoLocation(context: ActionContext<AccountState, any>): void {
            Geolocation.getCurrentPosition({ timeout: 10000, enableHighAccuracy: true })
                .then((position) => {
                    context.commit('SET_USER_LOCATION', position.coords);
                    context.commit('UPDATE_GEOLOCATION_STATUS', true);
                    const locationsFound = context.getters.getLimitedNearLocations.length > 0;
                    if (context.getters.isFirstSearch || !locationsFound) {
                        context.dispatch('setNearLocationsByCoordinates', context.getters.userCoordinates);
                    }
                })
                .catch(() => {
                    context.commit('UPDATE_GEOLOCATION_STATUS', false);
                });
        },
        setLogin(context: ActionContext<AccountState, any>) {
            if (context.getters.isLoginProcessing || context.getters.loggedIn) return;
            context.commit('SET_LOGIN_PROCESSING', true);
            initUserLogin(context);
        },
        setLoginRedirectUrl(context: ActionContext<AccountState, any>, url = '/') {
            context.commit('SET_LOGIN_REDIRECT_URL', url);
        },
        async setLogout(context: ActionContext<AccountState, any>, redirectUrl = '/location'): Promise<void> {
            await clearStorage();
            deleteLocalStorage(localStorageEnum.orderTip);
            context.commit('CLEAR_ACCOUNT_INFO');
            context.dispatch('logoutCart');
            logout()
                .then(() => router.push(redirectUrl))
                .catch(() => clearStorage());
        },
        addUserAddress(context: ActionContext<AccountState, any>, request: CustomerAddress): Promise<Address> {
            request.customerId = context.state.account?.customerId ?? '';
            return new Promise((resolve, reject) => {
                validateDeliveryAddress(new Address(request))
                    .then(async (valid) => {
                        request.storeNumber = valid.storeNumber;
                        if (valid.message.includes('address is corrected.')) {
                            const address1 = valid.address.address1;
                            const address2 = valid.address.address2 ?? '';
                            const city = valid.address.city ?? '';
                            const state = valid.address.state ?? '';
                            const zipCode = valid.address.zip ?? valid.address.zipcode ?? '';

                            const alert = await alertController.create({
                                header: `Address Suggestion`,
                                message: `${address1} ${address2}<br>${city}, ${state} ${zipCode}`,
                                backdropDismiss: true,
                                cssClass: 'valid-address-alert',
                                buttons: [
                                    {
                                        text: 'Check Address',
                                        role: 'cancel',
                                        handler: () => reject('Please validate your delivery address.'),
                                    },
                                    {
                                        text: 'Use This Address',
                                        role: 'confrim',
                                        handler: () => {
                                            //@ts-ignore // set validated/corrected address fields
                                            request.zip = valid.address.zipcode ?? valid.address.zipCode ?? valid.address.zip;
                                            request.address1 = valid.address.address1;
                                            request.city = valid.address.city;
                                            request.state = valid.address.state;
                                            addAccountAddressPost(request)
                                                .then((res) => {
                                                    request.addressId = res.addressId;
                                                    context.commit('ADD_ACCOUNT_ADDRESS', request);
                                                    context.dispatch('foundDeliveryLocation', valid);
                                                    resolve(valid.address);
                                                })
                                                .catch((err) => reject(err));
                                        },
                                    },
                                ],
                            });
                            return alert.present();
                        } else {
                            addAccountAddressPost(request)
                                .then((res) => {
                                    //@ts-ignore // set validated/corrected address fields
                                    request.zip = valid.address.zipcode ?? valid.address.zipCode ?? valid.address.zip;
                                    request.address1 = valid.address.address1;
                                    request.city = valid.address.city;
                                    request.state = valid.address.state;
                                    request.addressId = res.addressId;
                                    context.commit('ADD_ACCOUNT_ADDRESS', request);
                                    context.dispatch('foundDeliveryLocation', valid);
                                    resolve(valid.address);
                                })
                                .catch((err) => reject(err));
                        }
                    })
                    .catch((err) => reject(err));
            });
        },
        editUserAddress(context: ActionContext<AccountState, any>, request: CustomerAddress): Promise<Address> {
            request.customerId = context.state.account?.customerId ?? '';
            return new Promise((resolve, reject) => {
                validateDeliveryAddress(new Address(request))
                    .then(async (valid) => {
                        request.storeNumber = valid.storeNumber;
                        if (valid.message.includes('address is corrected.')) {
                            const address1 = valid.address.address1;
                            const address2 = valid.address.address2 ?? '';
                            const city = valid.address.city ?? '';
                            const state = valid.address.state ?? '';
                            const zipCode = valid.address.zip ?? valid.address.zipcode ?? '';

                            const alert = await alertController.create({
                                header: `Address Suggestion`,
                                message: `${address1} ${address2}<br>${city}, ${state} ${zipCode}`,
                                backdropDismiss: true,
                                cssClass: 'valid-address-alert',
                                buttons: [
                                    {
                                        text: 'Check Address',
                                        role: 'cancel',
                                        handler: () => reject('Please validate your delivery address.'),
                                    },
                                    {
                                        text: 'Use This Address',
                                        role: 'confrim',
                                        handler: () => {
                                            //@ts-ignore // set validated/corrected address fields
                                            request.zip = valid.address.zipcode ?? valid.address.zipCode ?? valid.address.zip;
                                            request.address1 = valid.address.address1;
                                            request.city = valid.address.city;
                                            request.state = valid.address.state;
                                            editAccountAddressPost(request)
                                                .then(() => {
                                                    context.commit('EDIT_ACCOUNT_ADDRESS', request);
                                                    context.dispatch('foundDeliveryLocation', valid);
                                                    resolve(valid.address);
                                                })
                                                .catch((err) => reject(err));
                                        },
                                    },
                                ],
                            });
                            return alert.present();
                        } else {
                            editAccountAddressPost(request)
                                .then(() => {
                                    context.commit('EDIT_ACCOUNT_ADDRESS', request);
                                    context.dispatch('foundDeliveryLocation', valid);
                                    resolve(valid.address);
                                })
                                .catch((err) => reject(err));
                        }
                    })
                    .catch((err) => reject(err));
            });
        },
        deleteUserAddress(context: ActionContext<AccountState, any>, request: CustomerAddress): Promise<any> {
            return new Promise((resolve, reject) => {
                deleteAddress(request.asDelete)
                    .then(() => resolve(context.commit('DELETE_ACCOUNT_ADDRESS', request)))
                    .catch((err) => reject(err));
            });
        },
        setTempAccountDetails(context: ActionContext<AccountState, any>, details: CheckoutRequest): Promise<any> {
            context.commit('ADD_TEMP_ACCOUNT_DETAIL', details);
            return Promise.resolve();
        },
        addSavedCard(context: ActionContext<AccountState, any>, card: CreditCard): Promise<CreditCard | void> {
            return addCreditCard(card)
                .then((res) => {
                    card.creditCardId = res.creditCardID;
                    context.commit('ADD_SAVED_CARD', card);
                    Promise.resolve(card);
                })
                .catch((error) => Promise.reject(error));
        },
        setSavedCards(context: ActionContext<AccountState, any>): Promise<CreditCard[] | void> {
            return fetchStoredCards(context.state.account?.customerId ?? '')
                .then((card) => {
                    context.commit('SET_SAVED_CARDS', card);
                    Promise.resolve(card);
                })
                .catch((error) => Promise.reject(error));
        },
        updateSavedCard(context: ActionContext<AccountState, any>, card: CreditCard): Promise<CreditCard | void> {
            return updateStoredCard(card)
                .then(() => {
                    context.commit('UPDATE_SAVED_CARD', card);
                })
                .catch((error) => Promise.reject(error));
        },
        deleteSavedCard(context: ActionContext<AccountState, any>, card: CreditCard): Promise<CreditCard | void> {
            return deleteStoredCard(card.asDelete())
                .then(() => {
                    context.commit('DELETE_SAVED_CARD', card);
                })
                .catch((error) => Promise.reject(error));
        },
        refreshAccountInfo(context: ActionContext<AccountState, any>) {
            return new Promise((resolve, reject) => {
                fetchAccountInfo(1)
                    .then((res) => resolve(context.commit('SET_ACCOUNT_INFO', res)))
                    .catch((error) => reject(error));
            });
        },
        async fetchOrderHistory(context: ActionContext<AccountState, any>) {
            context.commit('SET_ORDER_HISTORY_LOADING', true);
            if (!context.state.account) return;
            try {
                const response = await orderHistory(context.state.account?.customerId);
                if (response) context.commit('SET_ORDER_HISTORY', response);
            } catch (error) {
                errorToast('Unable to load order history at this time. Please try again later.');
            } finally {
                context.commit('SET_ORDER_HISTORY_LOADING', false);
            }
        },
        setAppInfo(context: ActionContext<AccountState, any>, info) {
            context.commit('SET_APP_INFO', info);
        },
    },
    getters: {
        isLoginProcessing: (state: AccountState): boolean => state.loginProcessing,
        isLoyaltyProcessing: (state: AccountState): boolean => state.loyaltyProcessing,
        isLoyaltyInfoProcessing: (state: AccountState): boolean => state.loyaltyInfoProcessing,
        getIsRewardsLocked: (state: AccountState): boolean => state.isRewardsLocked,
        getUpdatingRewardBasket: (state: AccountState): boolean => state.updatingRewardBasket,
        isLoggedIn: (state: AccountState): boolean => state.isAuthenticated,
        isCreateAccountProcessing: (state: AccountState): boolean => state.createAccountProcessing,
        userCoordinates: (state: AccountState): Coordinate => state.userLocation ?? DEFAULT_COORDINATES,
        getAccount: (state: AccountState): Account | undefined => state.account,
        getRewardsInfo: (state: AccountState): RewardsInfo | undefined => state.rewardsInfo,
        getRewardsBasketInfo: (state: AccountState): RewardsBasketInfo | null => state.rewardsBasketInfo,
        getIsRewardsHistoryLoading: (state: AccountState): boolean => state.isRewardsHistoryLoading,
        getRewardHistory: (state: AccountState): ReadonlyArray<RewardHistory> | null => state.rewardHistory,
        isOrderHistoryLoading: (state: AccountState): boolean => state.isOrderHistoryLoading,
        getOrderHistory: (state: AccountState): HistoryOrder[] | undefined => state.orderHistory,
        getHasOrderHistory: (state: AccountState): boolean => !!state.orderHistory && state.orderHistory.length > 0,
        getOrderHistoryCount: (state: AccountState): number => (state.orderHistory ? state.orderHistory.length : 0),
        getRewardErrors: (state: AccountState): RewardErrors => state.rewardErrors,
        getAccountOrGuest: (state: AccountState): Account => state.account ?? state.guest,
        getAccountAddresses: (state: AccountState): CustomerAddress[] => state.account?.deliveryAddresses ?? [],
        getAccountFirstName: (state: AccountState): string | undefined => state.account?.firstName,
        getAccountId: (state: AccountState): string | undefined => state.account?.customerId,
        getAccountSavedCards: (state: AccountState): CreditCard[] => state.account?.savedCards ?? [],
        getAccountDefaultCard: (state: AccountState): CreditCard | undefined => state.account?.savedCards.find((card) => card.isDefaultCard) ?? undefined,
        isGeoLocationOn: (state: AccountState): boolean => state.isGeoLocationOn,
        getLoginRedirectUrl: (): string => getLocalStorage(localStorageEnum.loginRedirectUrl) ?? '/',
        getAppInfo: (state: AccountState): any => state.appInfo,
    },
};
